import React from 'react'
import MainLayout from '@toasttab/shared/src/layout/MainLayout'
import Footnotes from '@toasttab/shared/src/components/organisms/Footnotes/Footnotes'
import SEO from '@toasttab/main/src/components/Common/SEO/SEO'
import { useSiteMetadata } from '@toasttab/main/src/hooks/use-site-metadata'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import {
  getAllEntries,
  getLanguageByCountryCode,
  getSiteIdByLanguageCode
} from '@toasttab/shared/src/utils/siteUtils'
import {
  PageContextProps,
  PageContextProvider
} from '@toasttab/shared/src/hooks/usePageContext'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { graphql } from 'gatsby'
import withNinetailedExperience from '@toasttab/shared/src/utils/Ninetailed/withNinetailedExperience'
import SnowflakeRenderer from '@toasttab/main/src/utils/SnowflakeRenderer'
import HomepageRegion from '@toasttab/main/src/components/Ninetailed/NT_HomepageRegion'
import Sites from '@toasttab/shared/src/data/Sites.json'
import { useFooterContent } from '@toasttab/shared/src/utils/layoutUtils'

export const query = graphql`
  query ($locale: String!, $region: String!, $regionId: String!) {
    contentfulMdsSnowflakeRegion(
      node_locale: { eq: $locale }
      country: { eq: $region }
      contentful_id: { eq: $regionId }
    ) {
      ...SnowflakeRegion
      nt_experiences {
        ...NT_SnowflakeRegion
      }
    }
    allContentfulGlobalPageHeader(filter: { node_locale: { eq: $locale } }) {
      edges {
        node {
          contentful_id
          id: contentful_id
          internalName
          node_locale
          pageHeader {
            country
            ...HeaderContent
            node_locale
          }
          nt_experiences {
            ...NT_GlobalPageHeader
          }
        }
      }
    }
    allContentfulGlobalPageFooter(filter: { node_locale: { eq: $locale } }) {
      edges {
        node {
          internalName
          node_locale
          pageFooter {
            country
            ...FooterContent
            node_locale
          }
        }
      }
    }
    allContentfulMicrocopy(filter: { node_locale: { eq: $locale } }) {
      edges {
        node {
          key
          content
        }
      }
    }
  }
`

const Home = (props: any) => {
  const { data, pageContext } = props
  const { globalSettings } = pageContext.globalData

  // Get header data from page query
  const headerData = data.allContentfulGlobalPageHeader?.edges?.find(
    (f: any) => f.node.internalName === 'Main Site Header'
  )?.node

  // Get footer data from page query
  const footerData = data.allContentfulGlobalPageFooter?.edges?.find(
    (f: any) => f.node.internalName === 'Main Site Footer'
  )?.node?.pageFooter

  // Get microcopy from page query
  const microCopyData = data.allContentfulMicrocopy?.edges

  const { language, region } = useI18next()
  const siteId = getSiteIdByLanguageCode(getLanguageByCountryCode(language))
  const node = pageContext
  const regionSite = data.contentfulMdsSnowflakeRegion
  const localizedEntries = getAllEntries('/', node.regions)

  const siteMetadata = useSiteMetadata()

  // Memoize footer content
  const footerContent = useFooterContent({
    footerData,
    footerOverride: node.footerOverride,
    region
  })

  const context: PageContextProps = {
    allEntries: localizedEntries,
    disclaimers: regionSite.footnotes,
    footerContent,
    globalSettings,
    headerData,
    headerOverride: node.headerOverride,
    language,
    microCopyData,
    packageName: 'main',
    region: region as 'US' | 'UK' | 'CA' | 'IE' | 'AU',
    regions: node.regions,
    siteId,
    siteMetadata,
    styleContext: 'hpSnowflake',
    title: regionSite.metaTitle,
    url: '/'
  }

  if (regionSite.nt_experiences) {
    const pageComponent = true
    return withNinetailedExperience(
      HomepageRegion,
      { ...regionSite, context },
      pageComponent
    )
  }

  const refContent = regionSite?.referencedContent

  return (
    <PageContextProvider value={context}>
      <MainLayout
        footerContent={footerContent}
        localizedEntries={localizedEntries}
        packageName='main'
        siteId={siteId}
        siteMetadata={siteMetadata}
      >
        {Array.isArray(refContent) && refContent.length > 0
          ? SnowflakeRenderer(refContent)
          : null}
        {regionSite.footnotes ? (
          <Footnotes footnotes={regionSite.footnotes} />
        ) : null}
      </MainLayout>
    </PageContextProvider>
  )
}

export default Home

export const Head = ({ data, pageContext }: any) => {
  const regionSite = data.contentfulMdsSnowflakeRegion
  const slug = ''
  const localizedEntries = Sites.sites.map((site: any) => {
    return {
      slug: '',
      language: site.language,
      siteId: site.regionCode,
      url: site.url
    }
  })
  return (
    <SEO
      description={regionSite?.metaDescription}
      fullUrl={pageContext.url}
      language={pageContext.i18n.language}
      localizedEntries={localizedEntries}
      pathname={slug}
      title={regionSite?.metaTitle}
      noIndex={
        typeof regionSite.isIndexed !== 'undefined' ||
        regionSite.isIndexed !== null
          ? !regionSite.isIndexed
          : false
      }
    />
  )
}
